import React, { useEffect, useMemo, useState } from 'react';
import { IResortPageInfo, IResortPathInfo, IResortShortWeatherInfo } from 'api/resort/types';
import { BottomMountains } from 'components/blocks/BottomMountains';
import { Footer } from 'components/blocks/Footer';
import { HeaderNav, HeaderNavCurLink } from 'components/common/HeaderNav';
import { HeadMeta } from 'components/common/HeadMeta';
import { Layout } from 'components/resort/Layout/Layout';
import { CurrentLink, CurrentTab } from 'components/resort/ResortHeaderNav/constants';
import { ResortHeaderNav } from 'components/resort/ResortHeaderNav';
import { AdUnitContext } from 'googlead/components/AdUnitContext';
import { TopAd } from 'googlead/components/TopAd';
import { I18nKey } from 'i18n/types';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { defaultImage } from 'page-components/resort/constants';
import { ResortFPCTracker } from 'util/facebookAnalytics/components/ResortFPCTracker';
import { IRelatedForResort } from 'api/region/types';
import { TemplateProps } from 'page-components/pathTypes';
import { isEmpty } from 'util/isEmpty';
import { ContactInformation } from 'components/resort/ContactInformation';
import { IAccommodationPage } from 'api/tripadvisor/types';
import { useOffers } from 'api/resort/useOffers';
import { accommodationSortingBySortType, sortAccommodationsByPrice } from 'util/accommodationsSortings';
import { SortByAccommodations, SortByAccommodationsDefault } from 'components/common/SortAccomodations/constants';
import { ITALodgingReq } from 'components/common/PlanYourTrip/types';
import { DateTime } from 'luxon';
import { useRouter } from 'next/router';
import { Routes } from 'routes';
import { RESORT_IDS_TO_HIDE_LODGING_FOR } from 'ots-constants';
import WeskiWisepopsPopupDiv from 'components/common/WeskiWisepopsPopupDiv';

const RESORT_TEMPLATE_NAMES_WITH_BOTTOM_WESKI_POPUP = new Set([
  'Resort Overview Resort',
  'Resort Reviews',
  'Resort Historical Snowfall',
  'Resort Lift Tickets',
]);

export interface IResortLayout extends TemplateProps {
  pathInfo: IResortPathInfo,
  resort: IResortPageInfo,
  shortWeather: IResortShortWeatherInfo,
  related: IRelatedForResort,
  adTKey: string,
  meta: {
    title: I18nKey['seo'],
    description: I18nKey['seo'],
    redirectTo: string | null,
    dynamic: { 'snow': { 'last24': number } },
  },
  navigation: {
    head: HeaderNavCurLink,
    tab: CurrentTab,
    link: CurrentLink,
  },
  hero: {
    title: I18nKey['common'],
    showVideo: boolean,
  },
  templateName: string;
  accommodations: IAccommodationPage | null;
}

const snowMeta = (report: IResortShortWeatherInfo) => {
  const { snow, temp, symbol } = (report || {});
  let Fs: string | undefined;
  let Rs: string | undefined;
  let Th: string | undefined;
  let Tl: string | undefined;

  // Today’s Weather Condition
  const Wc: string | undefined = symbol;

  if (snow) {
    // Forecasted Snow 72 hours
    // 0, 1-7, 8-15, 16-23, 24+
    // Projected snowfall over next 72 hours in cm’s.
    // If the amount falls in a range then we insert the range in the ad call.
    // e.g. If Forecasted snow is = 1,2,3,4,5,6,7 cm’s then the range of 1-7 would be inserted. If > = 24 cm then 24+ would be inserted.
    if (!isEmpty(snow.forecast72)) {
      Fs = '0';
      if (snow.forecast72 >= 0.5 && snow.forecast72 < 7.5) {
        Fs = '1-7';
      }
      if (snow.forecast72 >= 7.5 && snow.forecast72 < 15.5) {
        Fs = '8-15';
      }
      if (snow.forecast72 >= 15.5 && snow.forecast72 < 23.5) {
        Fs = '16-23';
      }
      if (snow.forecast72 >= 23.5) {
        Fs = '24plus';
      }
    }

    // Snow Reported 72 hours.
    // Resort reported snowfall over last 72 hours in cm’s.
    if (!isEmpty(snow.last72)) {
      Rs = '0';
      if (snow.last72 >= 0.5 && snow.last72 < 7.5) {
        Rs = '1-7';
      }
      if (snow.last72 >= 7.5 && snow.last72 < 15.5) {
        Rs = '8-15';
      }
      if (snow.last72 >= 15.5 && snow.last72 < 23.5) {
        Rs = '16-23';
      }
      if (snow.last72 >= 23.5) {
        Rs = '24plus';
      }
    }

    // Current Base Temp (Low)
    // Today’s Forecasted Low Temperature in Celsius.
    if (!isEmpty(temp.min)) {
      Tl = '--15';
      if (temp.min >= -14.5 && temp.min < -6.5) {
        Tl = '-14-7';
      }
      if (temp.min >= -6.5 && temp.min < 0.5) {
        Tl = '-6-0';
      }
      if (temp.min >= 0.5 && temp.min < 11.5) {
        Tl = '1-11';
      }
      if (temp.min >= 11.5) {
        Tl = '12plus';
      }
    }

    // Current Base Temp (High)
    // Today’s Forecasted High Temperature in Celsius.
    if (!isEmpty(temp.max)) {
      Th = '--15';
      if (temp.max >= -14.5 && temp.max < -6.5) {
        Th = '-14-7';
      }
      if (temp.max >= -6.5 && temp.max < 0.5) {
        Th = '-6-0';
      }
      if (temp.max >= 0.5 && temp.max < 11.5) {
        Th = '1-11';
      }
      if (temp.max >= 11.5) {
        Th = '12plus';
      }
    }
  }

  return ({
    // Forecasted Snow 72 hours
    Fs,
    Rs,
    Th,
    Tl,
    Wc,
  });
};

export const noOffersContext = React.createContext(false);

export const ResortLayout = ({
  children,
  pathInfo,
  resort,
  adTKey,
  meta,
  navigation,
  shortWeather,
  hero,
  templateName,
  related,
  accommodations,
}: IResortLayout & { children: React.ReactNode }) => {
  const { t: seoT } = useOTSTranslation('seo');
  const { t } = useOTSTranslation('common');
  const router = useRouter();
  const [hasNoOffers, setHasNoOffers] = useState(false);
  const shouldRenderBottomWeskiPopup = RESORT_TEMPLATE_NAMES_WITH_BOTTOM_WESKI_POPUP.has(templateName);

  const resortTitle = pathInfo.title_short ?? resort.title;
  const [sortBy] = useState<SortByAccommodations>(SortByAccommodationsDefault);

  const now = (DateTime.now().startOf('day'));
  const initialFromDate = now.plus({ days: 14 });
  const initialToDate = initialFromDate.plus({ days: 3 });

  const [formData] = useState<ITALodgingReq>({
    checkIn: DateTime.fromJSDate(initialFromDate.toJSDate()).toFormat('yyyy-MM-dd'),
    checkOut: DateTime.fromJSDate(initialToDate.toJSDate()).toFormat('yyyy-MM-dd'),
    numRooms: 1,
    numAdults: 2,
  });

  const locationList = useMemo(
    () => (accommodations?.data ?? []).map((a) => a.location_id).sort(),
    [accommodations],
  );

  const { offerMap, areOffersLoading } = useOffers(
    locationList,
    formData.checkIn,
    formData.checkOut,
    formData.numAdults,
    formData.numAdults,
  );

  const sorted = useMemo(() => {
    if (accommodationSortingBySortType[sortBy]) {
      return sortAccommodationsByPrice(accommodations?.data ?? [], offerMap, sortBy);
    }
    return (accommodations?.data ?? []);
  }, [(accommodations?.data ?? []), sortBy, offerMap]);

  const noOffers = useMemo(() => {
    if (areOffersLoading || !offerMap || !sorted.length) return true;

    return sorted.every((item) => {
      const offers = offerMap[item.location_id];
      return !(offers && offers.length && offers[0].clickUrl);
    });
  }, [areOffersLoading, offerMap, sorted]);

  if ((((noOffers || RESORT_IDS_TO_HIDE_LODGING_FOR.includes(resort.uuid)) && templateName === 'Resort Lodging')) && typeof window !== 'undefined') {
    router.push(Routes.resortTripPlanningOverview(pathInfo));
  }

  useEffect(() => {
    if (noOffers !== hasNoOffers) {
      setHasNoOffers(noOffers);
    }
  }, [noOffers]);

  return (
    <AdUnitContext
      region={resort.region}
      resort={pathInfo}
      adType="resort"
      adTKey={adTKey}
      meta={{
        snow: ((meta.dynamic?.snow?.last24 ?? 0) >= 7) ? 'yes' : 'no',
        ...snowMeta(shortWeather),
      }}
    >
      <ResortFPCTracker
        uuid={resort.uuid}
        region={pathInfo.region}
        shortTitle={pathInfo.title_short}
      />
      <HeadMeta
        title={seoT(meta.title, { resort: pathInfo.title })}
        description={seoT(meta.description, { resort: pathInfo.title })}
        image={resort?.image ?? defaultImage}
        redirectTo={meta.redirectTo}
      />
      <HeaderNav current={navigation.head} />
      <TopAd id={`resort-${pathInfo.uuid}`} templateName={templateName} />
      <div className="container-xl content-container">
        <ResortHeaderNav
          resort={pathInfo}
          tab={navigation.tab}
          link={navigation.link}
          liftsUrl={resort.liftsUrl}
          photo={resort?.image ?? defaultImage}
          title={t(hero.title, { resort: resort.title })}
          website={resort.website}
          phone={resort.phone}
          isPartner={resort.isPartner}
          video={hero.showVideo ? resort.video : undefined}
          region={resort.region}
          related={related}
          noOffers={noOffers}
        />
        <Layout className="layout-container">
          {meta.redirectTo ? (
            <div
              className="w-100"
              style={{
                height: '30vh', display: 'flex', justifyContent: 'center', alignItems: 'center',
              }}
            >
              <h3>
                You are being redirected to
                {' '}
                {meta.redirectTo}
                {' '}
                domain
              </h3>
            </div>
          ) : <noOffersContext.Provider value={hasNoOffers}>{children}</noOffersContext.Provider>}
        </Layout>

      </div>
      <ContactInformation
        title={resortTitle}
        uuid={resort.uuid}
      />
      <BottomMountains />
      <Footer />
      {shouldRenderBottomWeskiPopup && (
        <WeskiWisepopsPopupDiv isRegion={false} id={resort.uuid} />
      )}
    </AdUnitContext>
  );
};
